<template>
  <div class="container mx-auto mt-16 px-4 pb-20">
    <h1 class="font-bold text-4xl mb-8">
      {{ isNew ? "Ny kategori" : "Redigera kategori" }}
    </h1>

    <FormErrors :errors="errors" subject="category" />

    <form @submit.prevent="submit">
      <div class="flex flex-col mb-20 gap-5">
        <div class="mt-8">
          <Label>Namn</Label>
          <Input v-model="form.category.name" type="text" class="mt-2" />
        </div>

        <div>
          <Label>Undertitel</Label>
          <Input
            v-model="form.category.subtitle"
            type="text"
            class="mt-2"
            placeholder="e.g. Från surdeg till saftiga bullar"
          />
        </div>

        <div>
          <Label>Överordnad kategori</Label>
          <select
            v-model="form.category.parent_id"
            class="mt-2 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">Ingen</option>
            <option
              v-for="parent in parentCategories"
              :key="parent.id"
              :value="parent.id"
            >
              {{ parent.name }}
            </option>
          </select>
        </div>

        <div>
          <Label>Färg på kategori-etiketten</Label>
          <p class="text-sm text-muted-foreground mt-1 mb-3">Välj en färg — den visas på receptkorten som tillhör den här kategorin.</p>
          <div class="flex flex-wrap gap-3 mt-2">
            <button
              v-for="color in colorPalette"
              :key="color.value"
              type="button"
              class="w-9 h-9 rounded-full border-2 transition-all"
              :style="{ backgroundColor: color.value }"
              :class="form.category.color === color.value ? 'border-[#191c1e] scale-110' : 'border-transparent'"
              :title="color.label"
              @click="form.category.color = color.value"
            />
            <button
              v-if="form.category.color"
              type="button"
              class="w-9 h-9 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs hover:border-gray-400 transition-all"
              title="Ta bort färg"
              @click="form.category.color = ''"
            >✕</button>
          </div>
        </div>

        <div>
          <div v-if="category.cover_image_url" class="mt-2 mb-3">
            <img
              :src="category.cover_image_url"
              alt="Current cover image"
              class="h-40 w-auto rounded-lg object-cover"
            />
            <p class="text-sm text-muted-foreground mt-1">
              Nuvarande bild — ladda upp en ny för att ersätta den
            </p>
          </div>
          <Input
            type="file"
            accept="image/*"
            class="mt-2"
            @change="onFileChange"
          />
        </div>
      </div>

      <div class="mt-8 flex gap-4">
        <Button type="submit" :disabled="form.processing">
          {{ isNew ? "Create category" : "Update category" }}
        </Button>
        <Button as="a" :href="categoriesPath" variant="outline">
          Back to categories
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useForm } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import FormErrors from "@/components/form/FormErrors.vue";

const props = defineProps({
  category: { type: Object, required: true },
  parentCategories: { type: Array, default: () => [] },
  currentUser: { type: Object, default: null },
  actionPath: { type: String, required: true },
  formMethod: { type: String, required: true },
  categoriesPath: { type: String, required: true },
  categoryPath: { type: String, default: "" },
  errors: { type: Array, default: () => [] },
});

const isNew = computed(() => !props.category.id);

const form = useForm({
  category: {
    name: props.category.name ?? "",
    subtitle: props.category.subtitle ?? "",
    parent_id: props.category.parent_id ?? "",
    color: props.category.color ?? "",
    cover_image: null,
  },
});

const colorPalette = [
  { label: "Teal", value: "#14b8a6" },
  { label: "Bärnsten", value: "#f59e0b" },
  { label: "Violett", value: "#8b5cf6" },
  { label: "Rosa", value: "#ec4899" },
  { label: "Blå", value: "#3b82f6" },
  { label: "Röd", value: "#ef4444" },
  { label: "Grön", value: "#10b981" },
  { label: "Orange", value: "#f97316" },
];

function onFileChange(e) {
  form.category.cover_image = e.target.files[0] ?? null;
}

function submit() {
  const options = {};
  if (form.category.cover_image) {
    options.forceFormData = true;
  }

  form.transform((data) => ({
    category: {
      ...data.category,
      subtitle: data.category.subtitle === "" ? null : data.category.subtitle,
      parent_id:
        data.category.parent_id === "" ? null : data.category.parent_id,
    },
  }));

  if (isNew.value) {
    form.post(props.actionPath, options);
  } else {
    form.patch(props.actionPath, options);
  }
}
</script>
